import type { AttendanceDay } from '../api/types';
import type { WorkMode } from './attendance-readiness';
import { namaLayak } from './display-name';
import type { Member, WorkerProfile } from './worker-candidates';

/**
 * Daftar kehadiran BERNAMA untuk dashboard — MURNI, tanpa React dan tanpa jam sistem.
 *
 * Dashboard sebelumnya hanya menampilkan angka: "Hadir 3". Dengan tiga orang itu masih dapat
 * ditebak; dengan sepuluh, angka tanpa nama tidak dapat ditindaklanjuti — dan yang sebenarnya
 * dibutuhkan jam sembilan pagi bukan "berapa yang hadir", melainkan SIAPA YANG BELUM.
 *
 * Nama TIDAK disimpan di Workforce. Profil pekerja hanya memuat atribut operasional (nomor
 * pegawai, jabatan, kendaraan); identitasnya milik HBS. Jadi daftar ini adalah penggabungan tiga
 * sumber lewat `userId` — pola yang sama dengan halaman Karyawan, bukan pola baru.
 */

export interface RosterRow {
  readonly userId: string;
  readonly name: string;
  /** `null` berarti tidak ada catatan hari itu — yakni belum absen masuk. */
  readonly status: string | null;
  /** Mode kerja event hari itu (`null` bila belum ada catatan). Bukan sumbu pengelompokan. */
  readonly workMode: WorkMode | null;
  readonly firstCheckInAt: string | null;
  readonly lateMinutes: number;
}

export interface RosterSection {
  readonly key: string;
  readonly label: string;
  readonly rows: readonly RosterRow[];
}

/**
 * Ditampilkan saat `userId` tidak ada di roster.
 *
 * BUKAN `userId` mentah, dan bukan email. Orang yang membaca dashboard tidak dapat berbuat apa pun
 * dengan UUID, dan menampilkannya membocorkan pengenal internal ke layar yang dibuka bersama.
 * Pelajaran yang sama dengan sapaan beranda: lebih baik mengaku tidak tahu daripada menampilkan
 * sesuatu yang kebetulan tersedia.
 */
export const UNKNOWN_NAME = '(nama tidak diketahui)';

/** Urutan seksi. "Belum absen masuk" DI ATAS — itu satu-satunya seksi yang menuntut tindakan. */
const SECTIONS: readonly { readonly key: string; readonly label: string }[] = [
  { key: 'NONE', label: 'Belum absen masuk' },
  { key: 'LATE', label: 'Terlambat' },
  { key: 'PRESENT', label: 'Hadir' },
  { key: 'INCOMPLETE', label: 'Belum lengkap' },
  { key: 'ON_LEAVE', label: 'Cuti' },
];

function nameOf(members: readonly Member[], userId: string): string {
  const cocok = members.find((m) => m.userId === userId);
  return namaLayak(cocok?.displayName) ?? UNKNOWN_NAME;
}

/**
 * Menyusun seksi bernama untuk SATU tanggal.
 *
 * Tiga keputusan yang layak disebut:
 *
 * 1. **Hanya pekerja AKTIF yang muncul.** Yang nonaktif atau ditangguhkan tidak diharapkan absen,
 *    jadi memasukkannya ke "Belum absen masuk" akan menghasilkan daftar tindakan palsu yang
 *    memanjang setiap kali seseorang keluar.
 *
 * 2. **Hari yang ada TAPI statusnya tak dikenal tetap ditampilkan**, di seksinya sendiri. Status
 *    baru dapat lahir di server lebih dulu daripada di layar ini; membuangnya diam-diam akan
 *    membuat orang hilang dari dashboard justru ketika sesuatu yang tidak biasa terjadi padanya.
 *
 * 3. **Seksi kosong dibuang.** Layar ini dibaca di ponsel sambil berdiri; lima judul kosong
 *    mendorong satu-satunya daftar yang berisi ke bawah lipatan.
 */
export function buildAttendanceRoster(
  workers: readonly WorkerProfile[],
  members: readonly Member[],
  days: readonly AttendanceDay[],
  workDate: string,
): readonly RosterSection[] {
  const hariPerUser = new Map<string, AttendanceDay>();
  for (const hari of days) {
    if (hari?.workDate !== workDate) continue;
    if (typeof hari.userId !== 'string') continue;
    hariPerUser.set(hari.userId, hari);
  }

  const baris: RosterRow[] = [];
  for (const pekerja of workers) {
    if (pekerja?.employmentStatus !== 'ACTIVE') continue;
    const hari = hariPerUser.get(pekerja.userId);
    baris.push({
      userId: pekerja.userId,
      name: nameOf(members, pekerja.userId),
      status: hari?.status ?? null,
      workMode: hari?.workMode ?? null,
      firstCheckInAt: hari?.firstCheckInAt ?? null,
      lateMinutes: typeof hari?.lateMinutes === 'number' ? hari.lateMinutes : 0,
    });
  }

  const dikenal = new Set(SECTIONS.map((s) => s.key));
  const lain = baris.filter((r) => r.status !== null && !dikenal.has(r.status));

  const seksi: RosterSection[] = [];
  for (const { key, label } of SECTIONS) {
    const isi = baris.filter((r) => (key === 'NONE' ? r.status === null : r.status === key));
    if (isi.length === 0) continue;
    seksi.push({ key, label, rows: urutkan(isi) });
  }
  if (lain.length > 0) {
    seksi.push({ key: 'OTHER', label: 'Status lain', rows: urutkan(lain) });
  }
  return seksi;
}

/**
 * Yang punya jam absen masuk diurutkan menurut JAM — siapa mulai lebih dulu terbaca sekilas.
 * Yang tidak punya jam diurutkan menurut NAMA, supaya daftarnya tidak berpindah-pindah tiap muat.
 */
function urutkan(rows: readonly RosterRow[]): readonly RosterRow[] {
  return [...rows].sort((a, b) => {
    if (a.firstCheckInAt !== null && b.firstCheckInAt !== null) {
      return a.firstCheckInAt.localeCompare(b.firstCheckInAt);
    }
    if (a.firstCheckInAt !== null) return -1;
    if (b.firstCheckInAt !== null) return 1;
    return a.name.localeCompare(b.name, 'id');
  });
}

/**
 * Inisial untuk lingkaran avatar — MURNI.
 *
 * Dua huruf dari dua kata pertama; satu huruf bila namanya satu kata. Nama Indonesia sering
 * satu kata ("Malik", "Alif"), jadi memaksa dua huruf akan menghasilkan "MA" dan "AL" yang
 * terbaca seperti singkatan yang bukan inisial siapa pun.
 *
 * Nama yang tidak dikenal menghasilkan string KOSONG, bukan tanda tanya: lingkaran kosong
 * terbaca sebagai "belum ada", sedangkan "?" terbaca seperti kesalahan sistem.
 */
export function initialsOf(name: string): string {
  if (name === UNKNOWN_NAME) return '';
  const kata = name
    .trim()
    .split(/\s+/)
    .filter((k) => k.length > 0);
  if (kata.length === 0) return '';
  const huruf = kata.slice(0, 2).map((k) => k[0] ?? '');
  return huruf.join('').toUpperCase();
}
